import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown, Menu, X, ArrowUpRight } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { NAV } from "@/constants/testIds";
import { PRACTICE_AREAS } from "@/data/practiceAreas";
import { useBookConsultation } from "@/hooks/useBookConsultation";

const scrollToId = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth" });
};

// Child links nested under a specific practice area in the Practice Areas menu.
const PRACTICE_CHILDREN = {
  "corporate-structuring-incorporation": [
    { slug: "jurisdiction-atlas", title: "Jurisdiction Atlas", to: "/services/company-incorporation/jurisdiction-atlas" },
  ],
};

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [dropdown, setDropdown] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobilePractice, setMobilePractice] = useState(false);
  const location = useLocation();
  const bookConsultation = useBookConsultation();

  const isHome = location.pathname === "/";
  const overlay = isHome && !scrolled;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setMobilePractice(false);
  }, [location.pathname]);

  const textColor = overlay ? "text-canvas" : "text-charcoal";

  return (
    <motion.header
      initial={{ y: -40, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
      className={`fixed top-0 left-0 w-full z-50 transition-colors duration-500 ${
        overlay
          ? "bg-transparent border-b border-transparent"
          : "bg-canvas/90 backdrop-blur-xl border-b border-charcoal/10"
      }`}
    >
      <nav className="max-w-[1400px] mx-auto px-6 lg:px-12 h-20 flex items-center justify-between">
        {/* Logo */}
        <Link
          data-testid={NAV.logo}
          to="/"
          onClick={(e) => {
            if (isHome) {
              e.preventDefault();
              scrollToId("hero-section");
            }
          }}
          className="flex items-center"
          aria-label="AMADI home"
        >
          <img
            src="/amadi-logo.svg"
            alt="AMADI"
            className="h-8 md:h-9 w-auto"
          />
        </Link>

        {/* Desktop nav */}
        <div className="hidden lg:flex items-center gap-10">
          <Link
            data-testid={NAV.linkHome}
            to="/"
            onClick={(e) => {
              if (isHome) {
                e.preventDefault();
                scrollToId("hero-section");
              }
            }}
            className={`font-sans text-xs tracking-[0.18em] uppercase transition-colors duration-300 hover:text-gold ${textColor}`}
          >
            Home
          </Link>

          {/* Practice Areas dropdown (two-column) */}
          <div
            className="relative"
            onMouseEnter={() => setDropdown(true)}
            onMouseLeave={() => setDropdown(false)}
          >
            <button
              data-testid={NAV.practiceAreas}
              aria-haspopup="true"
              aria-expanded={dropdown}
              aria-controls="practice-dropdown-menu"
              onClick={() => setDropdown((v) => !v)}
              className={`flex items-center gap-1.5 font-sans text-xs tracking-[0.18em] uppercase transition-colors duration-300 hover:text-gold ${textColor}`}
            >
              Practice Areas
              <ChevronDown className="w-3 h-3" strokeWidth={1.5} />
            </button>
            <AnimatePresence>
              {dropdown && (
                <motion.div
                  id="practice-dropdown-menu"
                  data-testid={NAV.practiceDropdown}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 8 }}
                  transition={{ duration: 0.25, ease: "easeOut" }}
                  className="absolute left-1/2 -translate-x-1/2 top-full pt-4 w-[640px]"
                >
                  <div className="bg-white border border-charcoal/10 grid grid-cols-2">
                    {PRACTICE_AREAS.map((area, i) => {
                      const borderCls = `${i % 2 === 0 ? "border-r" : ""} ${i < PRACTICE_AREAS.length - 2 ? "border-b" : ""}`;
                      const children = PRACTICE_CHILDREN[area.slug];
                      if (children) {
                        return (
                          <div key={area.slug} className={`px-6 py-4 border-charcoal/[0.06] ${borderCls}`}>
                            <Link
                              to={`/practice/${area.slug}`}
                              data-testid={`dropdown-link-${area.slug}`}
                              onClick={() => setDropdown(false)}
                              className="group flex items-baseline gap-3 text-charcoal/80 hover:text-forest transition-colors duration-200"
                            >
                              <span className="font-sans text-[10px] text-gold tracking-[0.2em] pt-0.5">{area.index}</span>
                              <span className="font-serif text-sm leading-snug">{area.title}</span>
                            </Link>
                            <div className="mt-2 pl-7 flex flex-col gap-1.5">
                              {children.map((c) => (
                                <Link
                                  key={c.slug}
                                  to={c.to}
                                  data-testid={`dropdown-child-${c.slug}`}
                                  onClick={() => setDropdown(false)}
                                  className="inline-flex items-center gap-2 font-sans text-[11px] tracking-[0.04em] text-charcoal/60 hover:text-forest transition-colors duration-200"
                                >
                                  <span className="w-3 h-px bg-gold/70" />
                                  {c.title}
                                </Link>
                              ))}
                            </div>
                          </div>
                        );
                      }
                      return (
                        <Link
                          key={area.slug}
                          to={`/practice/${area.slug}`}
                          data-testid={`dropdown-link-${area.slug}`}
                          onClick={() => setDropdown(false)}
                          className={`group flex items-baseline gap-3 px-6 py-4 text-charcoal/80 hover:text-forest hover:bg-canvas transition-colors duration-200 border-charcoal/[0.06] ${borderCls}`}
                        >
                          <span className="font-sans text-[10px] text-gold tracking-[0.2em] pt-0.5">{area.index}</span>
                          <span className="font-serif text-sm leading-snug">{area.title}</span>
                        </Link>
                      );
                    })}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          <Link
            data-testid={NAV.linkAbout}
            to="/about"
            className={`font-sans text-xs tracking-[0.18em] uppercase transition-colors duration-300 hover:text-gold ${textColor}`}
          >
            About
          </Link>
          <Link
            data-testid={NAV.linkInsights}
            to="/insights"
            className={`font-sans text-xs tracking-[0.18em] uppercase transition-colors duration-300 hover:text-gold ${textColor}`}
          >
            Insights
          </Link>
          <Link
            data-testid={NAV.linkContact}
            to="/contact"
            className={`font-sans text-xs tracking-[0.18em] uppercase transition-colors duration-300 hover:text-gold ${textColor}`}
          >
            Contact
          </Link>
        </div>

        {/* CTA */}
        <button
          data-testid={NAV.bookCta}
          onClick={bookConsultation}
          className={`hidden lg:block font-sans text-xs tracking-[0.14em] uppercase font-bold px-7 py-3.5 border transition-colors duration-300 ${
            overlay
              ? "bg-transparent text-canvas border-canvas/40 hover:bg-canvas hover:text-charcoal"
              : "bg-forest text-canvas border-forest hover:bg-charcoal hover:border-charcoal"
          }`}
        >
          Book a Consultation
        </button>

        {/* Mobile toggle */}
        <button
          data-testid={NAV.mobileToggle}
          onClick={() => setMobileOpen((v) => !v)}
          aria-expanded={mobileOpen}
          aria-controls="mobile-menu"
          aria-label="Toggle menu"
          className={`lg:hidden ${overlay && !mobileOpen ? "text-canvas" : "text-charcoal"}`}
        >
          {mobileOpen ? <X className="w-6 h-6" strokeWidth={1.5} /> : <Menu className="w-6 h-6" strokeWidth={1.5} />}
        </button>
      </nav>

      {/* Mobile menu */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            id="mobile-menu"
            data-testid={NAV.mobileMenu}
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: "easeInOut" }}
            className="lg:hidden overflow-hidden bg-canvas border-b border-charcoal/10 max-h-[80vh] overflow-y-auto"
          >
            <div className="px-6 py-8 flex flex-col gap-6">
              <Link
                to="/"
                onClick={(e) => {
                  if (isHome) {
                    e.preventDefault();
                    scrollToId("hero-section");
                    setMobileOpen(false);
                  }
                }}
                className="text-left font-sans text-sm tracking-[0.18em] uppercase text-charcoal hover:text-forest transition-colors"
              >
                Home
              </Link>

              {/* Mobile Practice Areas accordion */}
              <div>
                <button
                  data-testid="mobile-practice-toggle"
                  onClick={() => setMobilePractice((v) => !v)}
                  aria-expanded={mobilePractice}
                  aria-controls="mobile-practice-list"
                  className="w-full flex items-center justify-between font-sans text-sm tracking-[0.18em] uppercase text-charcoal hover:text-forest transition-colors"
                >
                  Practice Areas
                  <ChevronDown className={`w-4 h-4 transition-transform duration-300 ${mobilePractice ? "rotate-180" : ""}`} strokeWidth={1.5} />
                </button>
                <AnimatePresence>
                  {mobilePractice && (
                    <motion.div
                      id="mobile-practice-list"
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3 }}
                      className="overflow-hidden"
                    >
                      <div className="mt-4 pl-4 flex flex-col gap-3 border-l border-charcoal/15">
                        {PRACTICE_AREAS.map((area) => (
                          <div key={area.slug} className="flex flex-col gap-2">
                            <Link
                              to={`/practice/${area.slug}`}
                              data-testid={`mobile-dropdown-link-${area.slug}`}
                              className="flex items-baseline gap-3 font-serif text-sm text-charcoal/75 hover:text-forest transition-colors"
                            >
                              <span className="font-sans text-[10px] text-gold tracking-[0.2em]">{area.index}</span>
                              {area.title}
                            </Link>
                            {(PRACTICE_CHILDREN[area.slug] || []).map((c) => (
                              <Link
                                key={c.slug}
                                to={c.to}
                                data-testid={`mobile-dropdown-child-${c.slug}`}
                                className="ml-7 inline-flex items-center gap-2 font-sans text-[12px] text-charcoal/60 hover:text-forest transition-colors"
                              >
                                <span className="w-3 h-px bg-gold/70" />
                                {c.title}
                              </Link>
                            ))}
                          </div>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              <Link to="/about" className="text-left font-sans text-sm tracking-[0.18em] uppercase text-charcoal hover:text-forest transition-colors">
                About
              </Link>
              <Link to="/insights" className="text-left font-sans text-sm tracking-[0.18em] uppercase text-charcoal hover:text-forest transition-colors">
                Insights
              </Link>
              <Link to="/contact" className="text-left font-sans text-sm tracking-[0.18em] uppercase text-charcoal hover:text-forest transition-colors">
                Contact
              </Link>

              <button
                onClick={bookConsultation}
                className="mt-2 inline-flex items-center justify-center gap-2 font-sans text-xs tracking-[0.14em] uppercase font-bold px-7 py-4 bg-forest text-canvas"
              >
                Book a Consultation
                <ArrowUpRight className="w-4 h-4" strokeWidth={1.5} />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
